import { useMemo } from "react";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../redux/store";
import {
    addExtra,
    addFinancePackExtras,
    addFinancePackServiceProducts,
    addServiceProduct,
    removeExtra,
    removeFinancePackExtra,
    removeFinancePackServiceProducts,
    removeServiceProduct,
    setWheel,
} from "../../../redux/actions/BasketActions";
import { find } from "../../../../../../common-deprecated/utils";
import type {
    ExtraType,
    PackType,
    ServiceProductType,
    WheelType,
} from "../../../../../../common-deprecated/types/CommonTypes";
import { trackBasketRemoveExtra, trackCustomizeConfigItem } from "../../../utils/tracking";
import type { FinancePackConfigurablesType } from "../../../../../shared-logic/types/FinanceTypes";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

type UseFinancePackExtrasReturnType = {
    isChecked: boolean;
    onChange: (isChecked: boolean, useBasketTracking?: boolean) => void;
};

const useFinancePackConfigurables = (
    configurable: FinancePackConfigurablesType,
    pack: PackType,
): UseFinancePackExtrasReturnType => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const basket = useBnbSelector((state) => state.basket);
    const financeOption = useBnbSelector((state) => state.utils.financeOption);
    const items = useBnbSelector((state) => state.items);
    const step = useBnbSelector((state) => state.navigation.step);

    const isChecked = useMemo(() => {
        if (financeOption === "cash") {
            return !!find(configurable.id, basket.extras) || !!find(configurable.id, basket.serviceProducts);
        } else {
            return (
                !!find(configurable.id, basket.financePackExtras) ||
                !!find(configurable.id, basket.financePackServiceProducts)
            );
        }
    }, [configurable.id, basket]);

    const onChange = (isActive: boolean, useBasketTracking?: boolean): void => {
        const removeExtrasTracking = (useTracking?: boolean): void => {
            if (useTracking) {
                trackBasketRemoveExtra(step);
            } else {
                trackCustomizeConfigItem(step, "remove-extra");
            }
        };

        if (financeOption === "cash") {
            if (isActive) {
                if (find(configurable.id, items.extras)) {
                    removeExtrasTracking(useBasketTracking);
                    dispatch(removeExtra(configurable as ExtraType, true, true));
                }
                if (find(configurable.id, items.serviceProducts)) {
                    removeExtrasTracking(useBasketTracking);
                    dispatch(removeServiceProduct(configurable as ServiceProductType, true));
                }
            } else {
                if (find(configurable.id, items.extras)) {
                    trackCustomizeConfigItem(step, "add-extra");
                    dispatch(addExtra(configurable as ExtraType, true));
                }
                if (find(configurable.id, items.serviceProducts)) {
                    trackCustomizeConfigItem(step, "add-extra");
                    dispatch(addServiceProduct(configurable as ServiceProductType, true, true));
                }
                if (find(configurable.id, items.wheels)) dispatch(setWheel(configurable as WheelType, true, true));
            }
            // This is the monthly part, on monthly the financePacks are selected.
        } else if (isActive) {
            if (find(configurable.id, pack.serviceProducts)) {
                removeExtrasTracking(useBasketTracking);
                dispatch(removeFinancePackServiceProducts(configurable as ServiceProductType, true));
            }
            if (find(configurable.id, pack.equipment)) {
                removeExtrasTracking(useBasketTracking);
                dispatch(removeFinancePackExtra(configurable as ExtraType, true));
            }
        } else {
            if (find(configurable.id, pack.serviceProducts)) {
                trackCustomizeConfigItem(step, "add-extra");
                dispatch(addFinancePackServiceProducts(pack, configurable as ServiceProductType, true));
            }
            if (find(configurable.id, pack.equipment)) {
                trackCustomizeConfigItem(step, "add-extra");
                dispatch(addFinancePackExtras(pack, configurable as ExtraType, true));
            }
        }
    };

    return {
        isChecked,
        onChange,
    };
};

export default useFinancePackConfigurables;
